/**
 * The widget's compiled consent copy (#869,
 * specs/012-widget-partner-portal/decisions/consent-template-partner.md).
 *
 * Request-scoped contact: the partner is the named party; the contractor it
 * assigns may contact the homeowner about THIS request, by a person dialling
 * or texting (no autodialer, recorded or AI voice — the brief page and the
 * offer email carry that rule to the contractor). Automated calls and texts
 * come only from the partner, who is named. So no contractor has to be named
 * before it is chosen.
 *
 * Consent copy is evidence: the server stores the paragraph the visitor saw
 * and compares it with the one it renders itself
 * (`server/src/services/lead-consent.ts`), so every function here has a
 * byte-identical twin there and a parity test that fails on drift. No
 * dependencies, so the server test can import this file directly.
 */

/** The SMS clause states a number because carriers filter "varies". */
export const SMS_MAX_MESSAGES = 6;

export const FINDERLY_PRIVACY_URL = 'https://app.finderlyfix.com/PrivacyPolicy';
export const FINDERLY_DO_NOT_SELL_URL = 'https://app.finderlyfix.com/DoNotSell';

/** P1 — the default paragraph in `pewc` mode (checkbox). */
export function pewcConsentText(partner: string, ctaLabel: string): string {
  return (
    `By checking this box and selecting "${ctaLabel}", I agree that ${partner}, and the contractor ` +
    `${partner} assigns to this request to act for it, may contact me at the number I entered about this repair ` +
    `request, even if my number is on a Do Not Call list. ${partner} may call using autodialed calls ` +
    'or a prerecorded, artificial or AI-generated voice. This is not a condition of any purchase.'
  );
}

/** The default paragraph in `consumer_directed` mode (beside the button). */
export function consumerDirectedConsentText(partner: string): string {
  return (
    `By sending this I agree that ${partner}, and the contractor ${partner} assigns to this request to act for it, ` +
    'may contact me about it using the contact details I provided.'
  );
}

/** P2 — the optional SMS box. Automated texts come from the partner only. */
export function smsConsentText(partner: string): string {
  return (
    `Text me about this request. ${partner} may send me automated texts about it at the number I ` +
    `entered, up to ${SMS_MAX_MESSAGES} messages. Msg & data rates may apply. Reply STOP to cancel, ` +
    'HELP for help. Not required to send my request.'
  );
}

/**
 * The re-confirmation box, shown only after the server answered
 * `409 WIDGET_CONTACT_RECONFIRM`: the number the visitor typed has a "stop
 * calling me" on file. Unticked by default, never required — the visitor may
 * send without it, and the request is then filed but not passed on.
 */
export function reconfirmContactText(partner: string): string {
  return (
    'You previously asked not to be called. Tick to allow ' +
    `${partner} and the contractor it assigns to contact you about this request.`
  );
}

/**
 * P3 — the optional eLocal box, shown only when the site's network fallback
 * is on and the network is eLocal. Calls only: carriers do not allow texting
 * consent collected for lead resale. Only eLocal (named) may autodial or
 * override Do Not Call; its unnamed buyers may only call by hand (a consent
 * must name "the seller", 47 CFR 64.1200(c)(2)(ii), (f)(9)). The server's
 * registry entry `partner-fallback/2026-10b` renders the same string.
 */
export function fallbackConsentText(): string {
  return (
    'If no contractor takes my request, FinderlyFix may send it to eLocal, a home-services referral ' +
    'network, and be paid for it. eLocal may sell it to other local service companies. eLocal may ' +
    'call me at the number I entered about this request, including autodialed calls and calls using ' +
    'a prerecorded, artificial or AI-generated voice, even if my number is on a Do Not Call list. ' +
    'Companies that buy my request from eLocal may only call me by hand and must respect the Do Not ' +
    'Call list unless I agree with them directly. Not required to send my request.'
  );
}

/** Whether P3 is offered: the site allows the fallback and the network is eLocal. */
export function fallbackOffered(routing: { finderly_network_fallback?: boolean; fallback_network?: string }): boolean {
  return routing.finderly_network_fallback === true && routing.fallback_network === 'elocal';
}

/**
 * The P4 sale clause on its own: the notice's eLocal sentence and the Do Not
 * Sell link. It is ours to give whenever the eLocal box is shown, so the form
 * renders it under the box when a partner's own notice replaces P4 — that
 * notice cannot be expected to say it.
 */
export function fallbackSaleNotice(): string {
  return (
    'If you tick the eLocal box, FinderlyFix sells your request to eLocal. ' +
    `[Do Not Sell or Share My Personal Information](${FINDERLY_DO_NOT_SELL_URL})`
  );
}

/** Shown above the Sound recorder when the partner set no `copy.audio_notice`:
 *  a clip can pick up other people in the room. */
export const DEFAULT_AUDIO_NOTICE =
  'This records a short sound clip. Anyone nearby may be picked up too, so let them know.';

/** P4 — the default `copy.notice_at_collection_md` (ours to show: Partner Terms §3; DPA §10 where signed). */
export function defaultNoticeAtCollection(partner: string, fallbackShown: boolean): string {
  const base =
    `FinderlyFix (AI Vision Technologies Inc) runs this form for ${partner} and sends ${partner} ` +
    `your request, contact details, photos and sound clips. [FinderlyFix privacy notice](${FINDERLY_PRIVACY_URL})`;
  return fallbackShown ? `${base}. ${fallbackSaleNotice()}` : base;
}
